/** Calcule comune pentru validare și pentru afișare. Aceleași formule ca în `physics/src/vehicle.rs`. */
import type { VehicleDefinition } from './types';

type Transmission = VehicleDefinition['transmission'];

export const AIR_DENSITY = 1.2;
export const ROLLING_RESISTANCE = 0.012;
export const DRIVELINE_EFFICIENCY = 0.88;
export const G = 9.81;

/** [lățime mm, profil %, jantă ţoli] */
export function parseTyre(size: string): [number, number, number] | null {
  const m = /^(\d+)\/(\d+) R(\d+)$/.exec(size.trim());
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}

export function tyreDiameterM(size: string): number | null {
  const p = parseTyre(size);
  return p ? (p[2] * 25.4 + (2 * p[0] * p[1]) / 100) / 1000 : null;
}

export function loadIndexKg(li: number): number {
  return 615 * Math.exp(0.0289 * (li - 91));
}

const RATING: Record<string, number> = { Q: 160, R: 170, S: 180, T: 190, H: 210, V: 240, W: 270, Y: 300 };
export function speedRatingKmh(r: string): number {
  return RATING[r] ?? 0;
}

export function curvePeakPowerKw(curve: Array<[number, number]>): number {
  return curve.reduce((m, [rpm, nm]) => Math.max(m, (nm * rpm * Math.PI) / 30 / 1000), 0);
}

export function powerAt(rpm: number, nm: number): number {
  return (nm * rpm * Math.PI) / 30 / 1000;
}

export function powerLimitedTopSpeedKmh(v: VehicleDefinition): number {
  const p = v.engine.maxPowerKw * 1000 * DRIVELINE_EFFICIENCY;
  const a = 0.5 * AIR_DENSITY * v.aero.cd * v.aero.frontalAreaM2;
  const r = ROLLING_RESISTANCE * v.mass.curbKg * G;
  let s = 30;
  for (let i = 0; i < 60; i++) s -= (a * s * s * s + r * s - p) / (3 * a * s * s + r);
  return s * 3.6;
}

export function overallRatio(t: Transmission, g: number): number | null {
  const fd = t.finalDrives.find((f) => f.gears.includes(g));
  const r = t.gears[g - 1];
  return fd && r !== undefined ? r * fd.ratio : null;
}

/** Viteza în fiecare treaptă la turația dată, km/h. */
export function speedInGearKmh(v: VehicleDefinition, g: number, rpm: number): number {
  const d = tyreDiameterM(v.tyres.rear.size) ?? tyreDiameterM(v.tyres.front.size) ?? 0.6;
  const r = overallRatio(v.transmission, g);
  return r ? (rpm / 60 / r) * Math.PI * d * 3.6 : 0;
}

export function gearingTopSpeedKmh(v: VehicleDefinition): number {
  let best = 0;
  for (let g = 1; g <= v.transmission.gears.length; g++) best = Math.max(best, speedInGearKmh(v, g, v.engine.limiter.rpm));
  return best;
}

export function boxInertia(v: VehicleDefinition): [number, number, number] {
  const m = v.mass.curbKg, l = v.dimensions.lengthMm / 1000, w = v.dimensions.widthMm / 1000, h = v.dimensions.heightMm / 1000;
  return [((m * (w * w + h * h)) / 12) * 0.85, ((m * (l * l + h * h)) / 12) * 0.8, ((m * (l * l + w * w)) / 12) * 0.85];
}

/** Valori derivate afișate în fișa tehnică. */
export function derived(v: VehicleDefinition) {
  const e = v.engine;
  const vd = e.displacementCc / 1e6;
  return {
    powerHp: e.maxPowerKw * 1.35962,
    kwPerLitre: e.maxPowerKw / (e.displacementCc / 1000),
    kgPerHp: v.mass.curbKg / (e.maxPowerKw * 1.35962),
    bmepBar: (4 * Math.PI * e.maxTorqueNm) / vd / 1e5,
    pistonSpeedMs: (2 * (e.strokeMm / 1000) * e.redlineRpm) / 60,
    frontAxleKg: v.mass.curbKg * v.mass.frontAxleFraction,
    rearAxleKg: v.mass.curbKg * (1 - v.mass.frontAxleFraction),
    topSpeedByPowerKmh: powerLimitedTopSpeedKmh(v),
    topSpeedByGearingKmh: gearingTopSpeedKmh(v),
    steeringLockDeg: (v.steering.turnsLockToLock * 360) / (2 * v.steering.rackRatio),
    cda: v.aero.cd * v.aero.frontalAreaM2
  };
}
